import React, { useState, useRef, useEffect } from "react";
import { useMutation, gql } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  List,
  ListItem,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

const TEST_BOT_MESSAGE = gql`
  mutation TestBotMessage(
    $widgetId: ID!
    $message: String!
    $conversationId: ID
  ) {
    testBotResponse(
      widgetId: $widgetId
      message: $message
      conversationId: $conversationId
    ) {
      botResponse
      shouldContinue
      handoffInitiated
      ragContext {
        sources {
          sourceTitle
          sourceUrl
          relevanceScore
          snippet
        }
        averageScore
      }
      intent
      error
    }
  }
`;

function TestChatPanel({ widgetId, botSettings, showAlert }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [showContext, setShowContext] = useState(false);
  const messagesEndRef = useRef(null);

  const [sendMessage, { loading: sending }] = useMutation(TEST_BOT_MESSAGE, {
    onCompleted: (data) => {
      const result = data.testBotResponse;

      // Add bot response
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          content: result.botResponse,
          timestamp: new Date().toISOString(),
          ragContext: result.ragContext?.sources || [],
          intent: result.intent,
          averageScore: result.ragContext?.averageScore,
          handoffInitiated: result.handoffInitiated,
          shouldContinue: result.shouldContinue,
          error: result.error,
        },
      ]);
    },
    onError: (error) => {
      showAlert(`Error: ${error.message}`, "error");
      // Add error message to chat
      setMessages((prev) => [
        ...prev,
        {
          role: "system",
          content: `Error: ${error.message}`,
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ]);
    },
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Add greeting message when component mounts
  useEffect(() => {
    if (botSettings?.greetingMessage && messages.length === 0) {
      setMessages([
        {
          role: "bot",
          content: botSettings.greetingMessage,
          timestamp: new Date().toISOString(),
          isGreeting: true,
        },
      ]);
    }
  }, [botSettings?.greetingMessage]);

  const handleSendMessage = () => {
    if (!inputMessage.trim() || sending) return;

    // Add user message
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: inputMessage,
        timestamp: new Date().toISOString(),
      },
    ]);

    // Send to API
    sendMessage({
      variables: {
        widgetId,
        message: inputMessage,
        conversationId,
      },
    });

    setInputMessage("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleResetConversation = () => {
    setMessages([]);
    setConversationId(null);
    if (botSettings?.greetingMessage) {
      setMessages([
        {
          role: "bot",
          content: botSettings.greetingMessage,
          timestamp: new Date().toISOString(),
          isGreeting: true,
        },
      ]);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Test Chat
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Test your AI Bot configuration with sample conversations
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowContext(!showContext)}
            startIcon={<InfoIcon />}
          >
            {showContext ? "Hide Context" : "Show Context"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleResetConversation}
            startIcon={<RefreshIcon />}
          >
            Reset Chat
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Chat Window */}
        <Grid item xs={12} md={showContext ? 8 : 12}>
          <Card
            sx={{ height: "500px", display: "flex", flexDirection: "column" }}
          >
            {/* Status Bar */}
            <Box
              sx={{
                px: 2,
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <BotIcon color="primary" />
              <Typography variant="subtitle2">AI Bot Test Mode</Typography>
              <Chip
                label={botSettings?.enabled ? "Enabled" : "Disabled"}
                size="small"
                color={botSettings?.enabled ? "success" : "default"}
              />
              {botSettings?.ragEnabled && (
                <Chip
                  label="RAG Active"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Messages Area */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                p: 2,
                backgroundColor: "#f5f5f5",
              }}
            >
              <List>
                {messages.map((message, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      flexDirection: "column",
                      alignItems:
                        message.role === "user" ? "flex-end" : "flex-start",
                      px: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        flexDirection:
                          message.role === "user" ? "row-reverse" : "row",
                        maxWidth: "80%",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor:
                            message.role === "user"
                              ? "primary.main"
                              : message.role === "system"
                              ? "grey.500"
                              : "secondary.main",
                        }}
                      >
                        {message.role === "user" ? (
                          <PersonIcon fontSize="small" />
                        ) : message.role === "system" ? (
                          <InfoIcon fontSize="small" />
                        ) : (
                          <BotIcon fontSize="small" />
                        )}
                      </Avatar>
                      <Paper
                        sx={{
                          p: 1.5,
                          backgroundColor:
                            message.role === "user"
                              ? "primary.main"
                              : message.isError
                              ? "error.light"
                              : "white",
                          color:
                            message.role === "user" || message.isError
                              ? "white"
                              : "text.primary",
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {message.content}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.7,
                            display: "block",
                            textAlign: "right",
                            mt: 0.5,
                          }}
                        >
                          {formatTime(message.timestamp)}
                        </Typography>
                      </Paper>
                    </Box>

                    {/* Debug Info */}
                    {message.role === "bot" && !message.isGreeting && (
                      <Box sx={{ mt: 0.5, ml: 5 }}>
                        {message.intent && (
                          <Chip
                            label={`Intent: ${message.intent}`}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        )}
                        {message.averageScore && (
                          <Chip
                            label={`RAG Score: ${(
                              message.averageScore * 100
                            ).toFixed(0)}%`}
                            size="small"
                            variant="outlined"
                            color={
                              message.averageScore > 0.7 ? "success" : "warning"
                            }
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        )}
                        {message.handoffInitiated && (
                          <Chip
                            label="Handoff Initiated"
                            size="small"
                            color="warning"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        )}
                        {message.error && (
                          <Chip
                            label={`Error: ${message.error}`}
                            size="small"
                            color="error"
                            sx={{ mb: 0.5 }}
                          />
                        )}
                      </Box>
                    )}
                  </ListItem>
                ))}
                <div ref={messagesEndRef} />
              </List>

              {sending && (
                <Box display="flex" alignItems="center" gap={1} ml={5} mt={1}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="textSecondary">
                    Bot is thinking...
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Input Area */}
            <Box
              sx={{
                p: 2,
                borderTop: 1,
                borderColor: "divider",
                display: "flex",
                gap: 1,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Type a message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={sending || !botSettings?.enabled}
                multiline
                maxRows={3}
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={
                  !inputMessage.trim() || sending || !botSettings?.enabled
                }
              >
                {sending ? <CircularProgress size={24} /> : <SendIcon />}
              </IconButton>
            </Box>
          </Card>
        </Grid>

        {/* Context Panel */}
        {showContext && (
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "500px", overflow: "auto" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  RAG Context
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {messages
                  .filter((m) => m.ragContext?.length > 0)
                  .slice(-1)
                  .map((message, idx) => (
                    <Box key={idx}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        gutterBottom
                      >
                        Sources used for last response:
                      </Typography>
                      {message.ragContext.map((ctx, ctxIdx) => (
                        <Paper
                          key={ctxIdx}
                          variant="outlined"
                          sx={{ p: 1.5, mb: 1.5 }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold">
                            {ctx.sourceTitle || "Unknown Source"}
                          </Typography>
                          {ctx.sourceUrl && (
                            <Typography
                              variant="caption"
                              color="primary"
                              component="a"
                              href={ctx.sourceUrl}
                              target="_blank"
                              rel="noopener"
                              sx={{ display: "block", mb: 1 }}
                            >
                              {ctx.sourceUrl}
                            </Typography>
                          )}
                          <Typography variant="body2" color="textSecondary">
                            {ctx.snippet}
                          </Typography>
                          <Chip
                            label={`Relevance: ${(
                              ctx.relevanceScore * 100
                            ).toFixed(0)}%`}
                            size="small"
                            color={
                              ctx.relevanceScore > 0.8 ? "success" : "warning"
                            }
                            sx={{ mt: 1 }}
                          />
                        </Paper>
                      ))}
                    </Box>
                  ))}

                {messages.filter((m) => m.ragContext?.length > 0).length ===
                  0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    textAlign="center"
                  >
                    No RAG context available yet. Send a message to see relevant
                    sources.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Instructions */}
      {!botSettings?.enabled && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: "warning.light",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="warning.dark">
            The AI Bot is currently disabled. Enable it in the Bot Settings tab
            to test the chat functionality.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default TestChatPanel;
